import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  clearChatTrails,
  fetchChatTrails,
  selecthasMoreChatTrails,
  selectChatTrails,
  selectChatTrailsLoading,
} from "../../../../store/slices/chat-trails";
import AuditTrailHeader from "../audit-trail-header";
import "../../styles.css";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import ApiSubjectsManagement from "../../../../api/subjects-manager";
import ApiTrails from "../../../../api/trail";
import moment from "moment";
import { Body } from "../../../../components/main-body/body";
import "../../../../components/main-body/body.css";
import classNames from "classnames";
import { selectSettings } from "../../../../store/slices/settings";
import ApiSiteManagement from "../../../../api/site-managements";

const ChatsTrailTable = () => {
  const trails = useSelector(selectChatTrails);
  const hasMore = useSelector(selecthasMoreChatTrails);
  const loading = useSelector(selectChatTrailsLoading);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [subjectsList, setSubjectsList] = useState([]);
  const settings = useSelector(selectSettings);
  const sitesEnabled = settings?.find((s) => s.key === "sitesEnable")?.value === true;
  const [sitesList, setSitesList] = useState([]);

  const fetchSubjectsList = () => {
    ApiSubjectsManagement.getSubjects().then((r) => {
      setSubjectsList(
        r.data.subjects.map((s) => ({
          id: s.displayId,
          name: s.displayId,
        }))
      );
    });
  };

  useEffect(() => {
    fetchSubjectsList();
  }, []);

  const getSites = async (deselect, query = "") => {
    return ApiSiteManagement.getSites({
      query,
    }).then(({ data }) => {
      const { sites } = data || {};
      setSitesList(sites?.map((c) => ({ id: c.id, name: c.name })));
    });
  };

  useEffect(() => {
    if (sitesEnabled) {
      getSites();
    }
  }, [settings, sitesEnabled]);

  const fetchData = (currentPage = page) => {
    if (!loading) {
      setPage(currentPage + 1);
      dispatch(
        fetchChatTrails({
          page: currentPage,
          ...filters,
          createdAtEnd: filters.period?.[filters.period.length - 1]?.end,
          createdAtStart: filters.period?.[filters.period.length - 1]?.start,
        })
      );
    }
  };

  const updateFilterValue = (filter) => {
    setFilters(filter);
  };

  useEffect(() => {
    if (trails) {
      dispatch(clearChatTrails());
    }
    fetchData(1);
  }, [filters]);

  const columns = [
    {
      title: t("trails.table.subjectId"),
      dataIndex: "id",
      render: (_, trail) => <span>{trail.subjectId || "N/A"}</span>,
      key: "id",
    },
    {
      title: t("trails.table.time"),
      dataIndex: "time",
      key: "time",
      render: (_, trail) => (
        <span>
          {`${moment(trail.createdAt)
            .utc()
            .format("DD MMM YYYY, HH:mm")}, UTC` || "N/A"}
        </span>
      ),
    },
    {
      title: t("trails.table.text"),
      dataIndex: "text",
      key: "text",
      render: (_, trail) => (
        <span style={{ wordWrap: "break-word", wordBreak: "break-word" }}>
          {
            trail.actionType === "messageFromAI"
              ? `AI chatbot sent message to subject "${trail.text}"`
              : trail.actionType === "messageToAI"
              ? `Subject sent message to AI chatbot "${trail.text}"`
              : trail.text || "N/A"
          }
        </span>
      ),
    },
  ];

  const actionTypes = [
    { id: "messageToAI", name: "Message to AI" },
    { id: "messageFromAI", name: "Message from AI" }
  ];

  const filterItems = [
    { name: "Date peroid", id: "period", period: true },
    { name: "Subject", id: "displayId", children: subjectsList },
    { name: "Action type", id: "actionType", children: actionTypes },
    ...(sitesEnabled ? [{ name: "Sites", id: "siteIds", children: sitesList }] : []),
  ];

  const exportTrail = () => {
    ApiTrails.exportChatTrails();
  };

  return (
    <Body disabledPaddings={true}>
      <AuditTrailHeader
        t={t}
        filterItems={filterItems}
        updateFilterValue={updateFilterValue}
        exportTrail={exportTrail}
      />
      <div className={classNames("lf-content")}>
        <InfinityTable
          data={trails}
          fetchData={fetchData}
          hasMore={!!hasMore}
          tableColumns={columns}
          isLoading={loading}
        />
      </div>
    </Body>
  );
};

export default ChatsTrailTable;
